import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChakraProvider, Box } from '@chakra-ui/react';
import Dashboard from './pages/Dashboard';
import Hyperunit from './pages/Hyperunit';
import Mayan from './pages/Mayan';
import Header from './components/Header';
import MenuBar from './components/MenuBar';
import Login from './components/Login';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function App() {
    const [summaryData, setSummaryData] = useState(null);
    const [configData, setConfigData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [showManagement, setShowManagement] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const handleLogin = (authenticated) => {
        setIsAuthenticated(authenticated);
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem('authenticated');
    };

    // Fetch data from API
    const fetchData = useCallback(async (showToast = false) => {
        try {
            if (showToast) setRefreshing(true);
            setError(null);

            const [summaryResponse, configResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/summary`),
                fetch(`${API_BASE_URL}/config`)
            ]);

            if (!summaryResponse.ok || !configResponse.ok) {
                throw new Error('Failed to fetch data from API');
            }

            const summary = await summaryResponse.json();
            const config = await configResponse.json();

            setSummaryData(summary);
            setConfigData(config);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Fetch data on component mount and set up auto-refresh
    useEffect(() => {
        if (isAuthenticated) {
            fetchData();

            // Auto-refresh every 30 seconds
            const interval = setInterval(() => fetchData(), 30000);

            return () => clearInterval(interval);
        }
    }, [fetchData, isAuthenticated]);

    // Login form component
    if (!isAuthenticated) {
        return <Login onLogin={handleLogin} />;
    }

    return (
        <Router>
            <Box minH="100vh" bg="gray.50">
                <Header onLogout={handleLogout} />
                <MenuBar
                    configData={configData}
                    lastUpdated={lastUpdated}
                    summaryData={summaryData}
                    refreshing={refreshing}
                    onRefresh={fetchData}
                    onToggleManagement={setShowManagement}
                    showManagement={showManagement}
                />

                <Routes>
                    <Route
                        path="/"
                        element={
                            <Dashboard
                                summaryData={summaryData}
                                configData={configData}
                                loading={loading}
                                error={error}
                                showManagement={showManagement}
                                setShowManagement={setShowManagement}
                                fetchData={fetchData}
                                refreshing={refreshing}
                                setRefreshing={setRefreshing}
                            />
                        }
                    />
                    <Route path="/hyperunit" element={<Hyperunit />} />
                    <Route path="/mayan" element={<Mayan />} />
                </Routes>
            </Box>
        </Router>
    );
}

function AppWrapper() {
    return (
        <ChakraProvider>
            <App />
        </ChakraProvider>
    );
}

export default AppWrapper;
